import { useRef, useEffect, RefObject } from 'react';
import { useAppDispatch, useAppSelector } from '../../../reduxService/hooks';
import { submitFailed } from '../../store/form/formControlSlice';
import { logDebug, logError, LogTag } from '../../utils/utils';
import { DynamicFormikProps } from '../../types/FormTypes';
import { createErrorPayload, handleValidationErrors, validateFormSafely, submitFormSafely } from '../utils/formSubmissionHelpers';

export function useFormSubmission(formikRef: RefObject<DynamicFormikProps>) {
    const dispatch = useAppDispatch();
    const submitTrigger = useAppSelector(state => state.formControl.submitTrigger);
    const lastTrigger = useRef(submitTrigger);

    useEffect(() => {
        let isCancelled = false; // Cleanup flag to prevent state updates after unmount

        const validateAndSubmit = async () => {
            if (isCancelled) return;
            if (!formikRef.current) {
                dispatch(submitFailed({ error: { message: 'Project data is still loading. Please wait and run again.' } }));
                return;
            }

            try {
                const errors = await validateFormSafely(formikRef, () => isCancelled);
                if (isCancelled || errors === null) return;

                if (Object.keys(errors).length > 0) {
                    logDebug('HOOK_SUBMIT', 'validateForm found errors', errors, LogTag.FORM_VALIDATION);
                    logDebug('HOOK_SUBMIT', 'Dispatching submitFailed due to validateForm errors', undefined, LogTag.FORM_SUBMISSION);
                    dispatch(submitFailed({ validationErrors: errors }));

                    if (!isCancelled) {
                        handleValidationErrors(errors, formikRef);
                    }
                } else {
                    logDebug('HOOK_SUBMIT', 'validateForm passed. Calling submitForm (expecting onSubmit)', undefined, LogTag.FORM_SUBMISSION);

                    if (isCancelled) return;

                    try {
                        await submitFormSafely(formikRef, () => isCancelled);
                    } catch (submitError) {
                        if (isCancelled) return;
                        logError('HOOK_SUBMIT', 'Error during submitForm call', submitError, LogTag.ERROR_HANDLING);
                        const errorPayload = createErrorPayload(submitError);
                        dispatch(submitFailed({ error: errorPayload }));
                    }
                }
            } catch (error) {
                if (isCancelled) return;

                logError('HOOK_SUBMIT', 'Error during submission process', error, LogTag.ERROR_HANDLING);
                const errorPayload = createErrorPayload(error);
                dispatch(submitFailed({ error: errorPayload }));
            }
        };

        if (submitTrigger > lastTrigger.current) {
            logDebug('HOOK_SUBMIT', 'Detected submit trigger', submitTrigger, LogTag.FORM_SUBMISSION);
            lastTrigger.current = submitTrigger;
            validateAndSubmit();
        }

        // Cleanup function to prevent memory leaks
        return () => {
            isCancelled = true;
        };
    }, [submitTrigger, dispatch, formikRef]);

    // This hook primarily handles side effects, doesn't need to return anything for now.
}
